import { Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import type {
  CustomColumnField,
  DraggableFieldType,
  DraggableMetaType,
  FieldWrapper,
} from "../../../../../../../../api/types/svTypes.ts";
import { AreaItemType, DimensionFieldType } from "../../../../../../../../api/types/svTypes.ts";
import { selectListFields } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { buildCustomColumnConfigChanges } from "../../../../utilities/fieldsHelper.ts";
import type { CustomColumnData } from "../../common/fields/custom-field/customColumn.types.ts";
import CustomFieldOptions from "../../common/fields/custom-field/CustomFieldOptions.tsx";
import CustomFormulaDialog from "../../common/fields/custom-field/CustomFormulaDialog.tsx";
import { findExistingCustomFormulaField } from "../../common/fields/custom-field/utilities/findExistingCustomFormulaField.ts";
import DropFieldContainer from "../../common/fields/DropFieldContainer.tsx";
import useCanAddCustomFields from "../../common/hooks/useCanAddCustomFields.ts";
import FieldOptionPopupContainer from "../../common/fields/measures/FieldOptionPopupContainer.tsx";
import useFieldsDropContainer from "../../common/table/hooks/useFieldsDropContainer.tsx";
import { getKeyValue } from "../../common/table/tableHelper.ts";
import { extractMeta } from "../../common/utilities/dropFieldContainerHelper.ts";
import { isDimensionMeta } from "../../common/utilities/metaUtils.ts";
import { createListField } from "../../utils/configuration/listConfiguration.ts";
import ListDimensionFieldOptions from "./ListDimensionFieldOptions.tsx";
import ListFreezeField from "./ListFreezeField.tsx";

interface Props {
  readonlyMode?: boolean;
}

const ListFieldsDropContainer = ({ readonlyMode = false }: Props) => {
  const fieldsAreaValues = useSelector(selectListFields);
  const canAddCustomField = useCanAddCustomFields();
  const [editedCustomFormulaField, setEditedCustomFormulaField] = useState<CustomColumnField>();

  const {
    createItem,
    handleRemove,
    closePopup,
    handleUpdateConfiguration,
    handleDimensionFieldCustomLabelChange,
    moveItem,
    addItem,
    anchorEl,
    currentField,
    removeItem,
    updateItemConfig,
  } = useFieldsDropContainer(fieldsAreaValues, { readonlyMode, showDateGroupingCaption: false });

  const handleCanDropItem = useCallback((item: FieldWrapper<DraggableFieldType> | DraggableMetaType) => {
    const meta = extractMeta(item);
    return meta !== undefined && isDimensionMeta(meta);
  }, []);

  const handleEditCustomFormula = (fieldGuid: string) => {
    setEditedCustomFormulaField(findExistingCustomFormulaField(fieldsAreaValues, fieldGuid));
    closePopup();
  };

  const handleApplyCustomFormula = (data: CustomColumnData) => {
    if (editedCustomFormulaField) {
      updateItemConfig(editedCustomFormulaField, buildCustomColumnConfigChanges(data));
    }
    setEditedCustomFormulaField(undefined);
  };

  return (
    <>
      <Stack sx={{ gap: ".5rem" }}>
        <DropFieldContainer
          title="Table Fields"
          areaFieldType={AreaItemType.FIELDS}
          acceptedDropTypes={[AreaItemType.DIMENSION, AreaItemType.CONDITIONS]}
          fields={fieldsAreaValues}
          canDropItem={handleCanDropItem}
          renderListItem={createItem}
          getKeyValue={getKeyValue}
          onAddItem={addItem}
          onRemoveItem={removeItem}
          onMoveItem={moveItem}
          createListItem={createListField}
          canAddCustomField={canAddCustomField}
          readonly={readonlyMode}
        />
        <FieldOptionPopupContainer anchorEl={anchorEl} cancel={closePopup}>
          {currentField?.type === DimensionFieldType.DIMENSION && (
            <ListDimensionFieldOptions
              field={currentField}
              cancel={closePopup}
              onRemove={handleRemove}
              onUpdateConfiguration={handleUpdateConfiguration}
              onUpdateCustomLabel={handleDimensionFieldCustomLabelChange}
              readonly={readonlyMode}
            />
          )}
          {currentField?.type === DimensionFieldType.CUSTOM && (
            <CustomFieldOptions
              field={currentField}
              otherOptions={<ListFreezeField field={currentField} readonly={readonlyMode} />}
              cancel={closePopup}
              saveChanges={handleUpdateConfiguration}
              onRemove={handleRemove}
              onEditCustomFormulaCalculationField={handleEditCustomFormula}
              readonly={readonlyMode}
            />
          )}
        </FieldOptionPopupContainer>
      </Stack>
      {editedCustomFormulaField && (
        <CustomFormulaDialog
          open
          onClose={() => setEditedCustomFormulaField(undefined)}
          onApply={handleApplyCustomFormula}
          editedField={editedCustomFormulaField}
          sourceFieldGuid={editedCustomFormulaField.config.guid}
        />
      )}
    </>
  );
};

export default ListFieldsDropContainer;
